// @ts-check
// Rascunho da seção "Personalização" do editor de campos (settings por tipo, valor padrão, único e índice) e a
// conversão para o que a API espera (doc 04, seções 2.3 e 2.5). Funções puras, sem Preact.
import { getFieldTypeDefinition } from '../../../../../shared/field-types/field-type-definitions.js';
import {
  DEFAULT_CURRENCY_CODE,
  DEFAULT_PHONE_COUNTRY_CODE,
  INDEXABLE_FIELD_TYPES,
  getUserSettingKeys,
  quoteDefault,
  unquoteDefault,
} from '../../../../../shared/field-types/field-settings.js';
import { isUuid } from '../../../../../shared/field-types/normalize-field-value.js';
import { findPhoneCountry } from '../../../lib/phone.js';

/**
 * @typedef {import('../../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {{ mode: 'none' | 'value' | 'now' | 'uuid', text: string, values: string[] }} DefaultDraft
 * @typedef {{ settings: Record<string, any>, defaultDraft: DefaultDraft, isUnique: boolean, isIndexed: boolean }} CustomizationDraft
 */

// tipos cujo padrão a tela edita; SELECT segue no editor de opções e os compostos sem formulário no Twenty ficam pela API
export const DEFAULT_VALUE_EDITOR_TYPES = Object.freeze(['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'DATE_TIME', 'RATING', 'MULTI_SELECT', 'CURRENCY', 'PHONES', 'UUID']);

/** @returns {DefaultDraft} */
const emptyDefault = () => ({ mode: 'none', text: '', values: [] });

/**
 * Padrão de um campo novo: moeda em reais e telefone no Brasil, como a escola espera.
 * @param {string} type
 */
export function initialDefaultValue(type) {
  if (type === 'CURRENCY') return { amountMicros: null, currencyCode: quoteDefault(DEFAULT_CURRENCY_CODE) };
  if (type === 'PHONES') return phoneDefault(DEFAULT_PHONE_COUNTRY_CODE);
  return null;
}

/** @param {string} countryCode */
function phoneDefault(countryCode) {
  const country = findPhoneCountry(countryCode);
  return { primaryPhoneCountryCode: quoteDefault(country.countryCode), primaryPhoneCallingCode: quoteDefault(country.callingCode) };
}

/**
 * @param {string} type @param {unknown} defaultValue no formato do Twenty
 * @returns {DefaultDraft}
 */
export function toDefaultDraft(type, defaultValue) {
  if (defaultValue === null || defaultValue === undefined) return emptyDefault();
  if (defaultValue === 'now') return { ...emptyDefault(), mode: 'now' };
  if (defaultValue === 'uuid') return { ...emptyDefault(), mode: 'uuid' };
  const value = /** @type {any} */ (defaultValue);
  switch (type) {
    case 'MULTI_SELECT':
      return { ...emptyDefault(), mode: 'value', values: Array.isArray(value) ? value.map((item) => String(unquoteDefault(item))) : [] };
    case 'CURRENCY':
      return { ...emptyDefault(), mode: 'value', text: String(unquoteDefault(value?.currencyCode) ?? '') };
    case 'PHONES':
      return { ...emptyDefault(), mode: 'value', text: String(unquoteDefault(value?.primaryPhoneCountryCode) ?? '') };
    case 'DATE_TIME':
      return { ...emptyDefault(), mode: 'value', text: toLocalDateTime(String(unquoteDefault(value))) };
    default:
      return { ...emptyDefault(), mode: 'value', text: String(unquoteDefault(value)) };
  }
}

/** ISO para o valor de um input datetime-local. @param {string} isoValue */
function toLocalDateTime(isoValue) {
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (/** @type {number} */ number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Valor padrão no formato do Twenty a partir do rascunho, ou a chave do erro de validação.
 * @param {string} type @param {DefaultDraft} draft
 * @returns {{ value: unknown, error: string | null }}
 */
export function fromDefaultDraft(type, draft) {
  if (draft.mode === 'none') return { value: null, error: null };
  if (draft.mode === 'now') return { value: 'now', error: null };
  if (draft.mode === 'uuid') return { value: 'uuid', error: null };
  const text = draft.text.trim();
  switch (type) {
    case 'TEXT':
      return { value: draft.text === '' ? null : quoteDefault(draft.text), error: null };
    case 'NUMBER': {
      if (text === '') return { value: null, error: null };
      const number = Number(text.replace(/\s/g, '').replace(',', '.'));
      return Number.isFinite(number) ? { value: number, error: null } : { value: null, error: 'fieldEditor.defaultValue.errors.number' };
    }
    case 'BOOLEAN':
      return { value: text === '' ? null : text === 'true', error: null };
    case 'DATE':
    case 'RATING':
      return { value: text === '' ? null : quoteDefault(text), error: null };
    case 'DATE_TIME': {
      if (text === '') return { value: null, error: null };
      const date = new Date(text);
      return Number.isNaN(date.getTime()) ? { value: null, error: 'fieldEditor.defaultValue.errors.date' } : { value: quoteDefault(date.toISOString()), error: null };
    }
    case 'MULTI_SELECT':
      return { value: draft.values.length === 0 ? null : draft.values.map(quoteDefault), error: null };
    case 'CURRENCY':
      return { value: text === '' ? null : { amountMicros: null, currencyCode: quoteDefault(text) }, error: null };
    case 'PHONES':
      return { value: text === '' ? null : phoneDefault(text), error: null };
    case 'UUID':
      if (text === '') return { value: null, error: null };
      return isUuid(text) ? { value: quoteDefault(text.toLowerCase()), error: null } : { value: null, error: 'fieldEditor.defaultValue.errors.uuid' };
    default:
      return { value: null, error: null };
  }
}

/**
 * Rascunho a partir do campo salvo (ou dos padrões de um campo novo).
 * @param {FieldMetadata | null} field @param {string} type
 * @returns {CustomizationDraft}
 */
export function toCustomizationDraft(field, type) {
  /** @type {Record<string, any>} */
  const settings = {};
  const keys = getUserSettingKeys(type, { isCreate: field === null, isCustom: field?.isCustom ?? true }).filter((key) => key !== 'isIndexed');
  for (const key of keys) {
    const value = field?.settings?.[key];
    if (value !== null && value !== undefined) settings[key] = value;
  }
  return {
    settings,
    defaultDraft: toDefaultDraft(type, field ? field.defaultValue : initialDefaultValue(type)),
    isUnique: Boolean(field?.isUnique),
    isIndexed: field?.settings?.isIndexed === true,
  };
}

/** O tipo oferece o liga e desliga de unicidade. @param {string} type */
export const canTypeBeUnique = (type) => getFieldTypeDefinition(type).canBeUnique;

/** O campo oferece o índice simples (personalizado e de coluna simples). @param {FieldMetadata | null} field @param {string} type */
export const canFieldBeIndexed = (field, type) => (field === null || Boolean(field.isCustom)) && INDEXABLE_FIELD_TYPES.includes(type);

/**
 * Por que a unicidade de um campo padrão não pode mudar (chave i18n), ou null quando pode.
 * @param {FieldMetadata | null} field
 */
export function getUniqueLockReason(field) {
  if (!field || field.isCustom) return null;
  if (field.isUIReadOnly) return 'fieldEditor.values.lockedSystem';
  if (field.isUnique && field.settings?.isUniqueSetByUser !== true) return 'fieldEditor.values.lockedPackage';
  return null;
}

/**
 * O que mandar à API: na criação, só o que foi escolhido; na edição, só o que mudou (null tira a escolha).
 * @param {FieldMetadata | null} field @param {string} type @param {CustomizationDraft} draft
 * @returns {{ changes: { settings?: Record<string, any>, defaultValue?: unknown, isUnique?: boolean }, error: string | null }}
 */
export function buildCustomizationChanges(field, type, draft) {
  const isCreate = field === null;
  /** @type {{ settings?: Record<string, any>, defaultValue?: unknown, isUnique?: boolean }} */
  const changes = {};
  const keys = getUserSettingKeys(type, { isCreate, isCustom: field?.isCustom ?? true });
  /** @type {Record<string, any>} */
  const desired = { ...draft.settings };
  if (keys.includes('isIndexed')) desired.isIndexed = draft.isIndexed && !draft.isUnique;
  /** @type {Record<string, any>} */
  const settings = {};
  for (const key of keys) {
    const next = desired[key] ?? null;
    const current = field?.settings?.[key] ?? null;
    if (isCreate ? next !== null && next !== false : next !== current && !(next === false && current === null)) settings[key] = next;
  }
  if (Object.keys(settings).length > 0) changes.settings = settings;

  if (DEFAULT_VALUE_EDITOR_TYPES.includes(type)) {
    const { value, error } = fromDefaultDraft(type, draft.defaultDraft);
    if (error) return { changes, error };
    if (isCreate ? value !== null : JSON.stringify(value) !== JSON.stringify(field?.defaultValue ?? null)) changes.defaultValue = value;
  }
  if (isCreate ? draft.isUnique : draft.isUnique !== Boolean(field?.isUnique)) changes.isUnique = draft.isUnique;
  return { changes, error: null };
}
